import { useState } from "react";
import type { HubInvitationAddress, StudentHubInvitations } from "./api.js";
import { ActionConfirmation } from "./ui/ActionConfirmation.js";
import { Dialog } from "./ui/Dialog.js";
import "./hubInvite.css";

type InviteProps = {
  studentName: string;
  invitations: StudentHubInvitations | null | undefined;
  sendInvitations: (recipients: string[], resend: boolean) => Promise<{ hubAccess: StudentHubInvitations }>;
  onChanged: () => void | Promise<void>;
  school?: boolean;
};

const labels = { parent: "Parent", student: "Student", secondary: "Second parent / contact" };

function addressStatus(address: HubInvitationAddress): string {
  switch (address.state) {
    case "linked": return "Already has Hub access";
    case "account_unlinked": return "Has a Hub account; access needs review";
    case "invitation_pending": return "Invitation already sent";
    default: return "Ready to invite";
  }
}

export function HubInviteButton(props: InviteProps) {
  const [open, setOpen] = useState(false);
  return <>
    <button type="button" className="quiet student-record-invite-action" onClick={() => setOpen(true)}>
      Invite to Hub
    </button>
    {open && <HubInviteDialog {...props} onClose={() => setOpen(false)} />}
  </>;
}

function HubInviteDialog({ studentName, invitations, sendInvitations, onChanged, school, onClose }: InviteProps & { onClose: () => void }) {
  const [current, setCurrent] = useState(invitations);
  const [selected, setSelected] = useState(invitations?.defaultRecipients ?? []);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [attention, setAttention] = useState(false);
  const enabled = current?.invitationsEnabled !== false;
  const available = current?.addresses.filter((address) => address.state === "no_account" || address.state === "account_unconfirmed") ?? [];
  const recipients = selected.filter((email) => available.some((address) => address.email === email));

  async function invite(emails: string[], resend = false) {
    if (busy || !enabled || emails.length === 0) return;
    setBusy(true);
    setProblem(null);
    setMessage(null);
    try {
      const { hubAccess } = await sendInvitations(emails, resend);
      setCurrent(hubAccess);
      setSelected([]);
      const failed = hubAccess.mailFailed ?? [];
      const queued = hubAccess.invited ?? [];
      setAttention(failed.length > 0);
      setMessage([
        ...(queued.length ? [`Invitation queued for ${queued.join(", ")}.`] : []),
        ...(failed.length ? [`The email to ${failed.join(", ")} could not be queued. Use Resend to try again.`] : []),
        ...(!queued.length && !failed.length ? ["No new invitation was needed. Check the address status below."] : []),
      ].join(" "));
      void Promise.resolve().then(onChanged).catch(() => {
        setProblem("The invitation result is shown, but the record could not be refreshed. Reload the record to check its latest status.");
      });
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "Could not send the Hub invitation.");
    } finally {
      setBusy(false);
    }
  }

  return <Dialog title="Invite to Hub" description={<>Choose who to invite for {studentName}. They will receive a link to set up or sign in to their Hub account.</>}
    onClose={onClose} size="small" busy={busy}>
    {message && <ActionConfirmation variant={attention ? "attention" : "success"} title={attention ? "Check invitation email" : "Hub invitation updated"} description={message} />}
    {problem && <p className="error small" role="alert">{problem}</p>}
    {!current ? (
      <p className="muted">{school
        ? "Review this pupil's family contact record in Admin before sending an invitation."
        : "Invitation details are unavailable. Reload this student record and try again."}</p>
    ) : current.addresses.length === 0 ? (
      <p className="muted">Add a parent or student email address in Admin, then invite them here.</p>
    ) : (
      <fieldset className="hub-invite-recipients" disabled={busy}>
        <legend>Send to</legend>
        {current.addresses.map((address) => {
          const canInvite = available.some((candidate) => candidate.email === address.email);
          return <div className="hub-invite-address" key={address.email}>
            <label className="hub-invite-recipient">
              <input type="checkbox" checked={canInvite && selected.includes(address.email)} disabled={!enabled || !canInvite}
                onChange={(event) => setSelected((previous) => event.target.checked
                  ? [...previous, address.email] : previous.filter((email) => email !== address.email))} />
              <span><strong>{labels[address.role]}</strong><span className="hub-invite-email">{address.email}</span>
                <span className="small muted">{addressStatus(address)}</span></span>
            </label>
            {enabled && address.state === "invitation_pending" && <button type="button" className="quiet small"
              disabled={busy} onClick={() => void invite([address.email], true)} aria-label={`Resend invitation to ${address.email}`}>Resend invitation</button>}
          </div>;
        })}
      </fieldset>
    )}
    {!enabled && <p className="muted small">Hub invitations are not available at the moment.</p>}
    <div className="ui-confirm-actions">
      <button type="button" className="action-secondary" disabled={busy} onClick={onClose}>Close</button>
      <button type="button" className="action-primary" disabled={busy || !enabled || recipients.length === 0}
        onClick={() => void invite(recipients)}>{busy ? "Sending…" : "Send invitation"}</button>
    </div>
  </Dialog>;
}
